import React, { useMemo } from 'react';
import { useWorkShifts, useMonthlySummary, useEmployees } from '@/hooks/useWorkShifts';
import { useAuth } from '@/contexts/AuthContext';
import { IWorkShift, ShiftType } from '@/types/workshift';

interface Holiday {
  date: string; // YYYY-MM-DD
  name: string;
}

interface WorkShiftCalendarProps {
  selectedYear: number;
  selectedMonth: number;
  onDateChange: (year: number, month: number) => void;
  selectedUser?: string;
  onUserChange?: (user: string) => void;
}

const mockHolidays: Holiday[] = [
  { date: '2025-12-25', name: 'Christmas Day' },
  { date: '2025-12-26', name: 'Boxing Day' },
  { date: '2025-01-01', name: "New Year's Day" },
  { date: '2025-02-28', name: 'Peace Memorial Day' },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WorkShiftCalendar: React.FC<WorkShiftCalendarProps> = ({
  selectedYear,
  selectedMonth,
  onDateChange,
  selectedUser,
  onUserChange,
}) => {
  const today = new Date();
  const { user } = useAuth();
  // TODO: deprecated — replace with role-based check from JWT when Google OAuth is used in production
  const isAdmin = user?.username === 'derick' || user?.username === 'april';

  // Use props for year/month (1-based month from parent)
  const year = selectedYear;
  const month = selectedMonth;

  // Fetch employees list (admin only)
  const { data: employees } = useEmployees();

  // Fetch work shifts from API (with optional user param for admin)
  const { data: shiftsData, isLoading, error } = useWorkShifts({
    year,
    month,
    user: isAdmin ? selectedUser : undefined,
  });
  const { data: summaryData } = useMonthlySummary({
    year,
    month,
    user: isAdmin ? selectedUser : undefined,
  });

  // Calculate calendar days for the current month (month is 1-based)
  const calendarDays = useMemo(() => {
    const monthIdx = month - 1; // Convert to 0-based for Date constructor

    const firstDayOfMonth = new Date(year, monthIdx, 1);
    const lastDayOfMonth = new Date(year, monthIdx + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay();
    const totalDaysInMonth = lastDayOfMonth.getDate();

    const days: (Date | null)[] = [];

    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= totalDaysInMonth; day++) {
      days.push(new Date(year, monthIdx, day));
    }

    return days;
  }, [year, month]);

  // Navigation handlers - call parent's onDateChange
  const goToPreviousMonth = () => {
    if (month === 1) {
      onDateChange(year - 1, 12);
    } else {
      onDateChange(year, month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      onDateChange(year + 1, 1);
    } else {
      onDateChange(year, month + 1);
    }
  };

  const goToToday = () => {
    onDateChange(today.getFullYear(), today.getMonth() + 1);
  };

  // Helper functions
  const formatDateString = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getShiftForDate = (date: Date): IWorkShift | undefined => {
    const dateStr = formatDateString(date);
    return shiftsData?.shifts?.find(shift => shift.date === dateStr);
  };

  const getHolidayForDate = (date: Date): Holiday | undefined => {
    const dateStr = formatDateString(date);
    return mockHolidays.find(holiday => holiday.date === dateStr);
  };

  const isToday = (date: Date): boolean => {
    return date.toDateString() === today.toDateString();
  };

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getShiftColor = (type: ShiftType): string => {
    switch (type) {
      case 'full-day':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'morning':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'afternoon':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'night':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'half-day':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600';
    }
  };

  const getShiftLabel = (type: ShiftType): string => {
    switch (type) {
      case 'full-day':
        return 'Full Day';
      case 'morning':
        return 'Morning';
      case 'afternoon':
        return 'Afternoon';
      case 'night':
        return 'Night';
      case 'half-day':
        return 'Half Day';
      default:
        return type;
    }
  };

  // Monthly statistics from API
  const monthlyStats = {
    totalHours: summaryData?.total_hours ?? 0,
    workDays: summaryData?.total_days ?? 0,
  };

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Failed to load work shifts. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Employee Selector (Admin Only) */}
      {isAdmin && employees && employees.length > 0 && onUserChange && (
        <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-700">
          <label className="text-sm font-medium text-purple-700 dark:text-purple-300">
            Employee:
          </label>
          <select
            value={selectedUser || user?.username || ''}
            onChange={(e) => onUserChange(e.target.value)}
            className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-purple-300 dark:border-purple-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {employees.map((emp) => (
              <option key={emp.username} value={emp.username}>
                {emp.name} ({emp.username})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Calendar Header with Navigation */}
      <div className="space-y-3">
        {/* Month Navigation Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label="Previous month"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white min-w-[120px] sm:min-w-[200px] text-center">
              {MONTHS[month - 1]} {year}
            </h2>

            <button
              onClick={goToNextMonth}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label="Next month"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <button
            onClick={goToToday}
            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
          >
            Today
          </button>
        </div>

        {/* Monthly Stats Row - separate row on mobile */}
        <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-4 text-xs sm:text-sm">
          <div className="px-2 py-1 sm:px-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
            <span className="text-gray-600 dark:text-gray-400">Days: </span>
            <span className="font-semibold text-blue-600 dark:text-blue-400">{monthlyStats.workDays}</span>
          </div>
          <div className="px-2 py-1 sm:px-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
            <span className="text-gray-600 dark:text-gray-400">Hours: </span>
            <span className="font-semibold text-green-600 dark:text-green-400">{monthlyStats.totalHours.toFixed(1)}h</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
        <span className="text-gray-600 dark:text-gray-400 font-medium">Legend:</span>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-purple-500 mr-2"></div>
          <span>Today</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-emerald-100 dark:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-700 mr-2"></div>
          <span>Holiday</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-600 mr-2"></div>
          <span>Weekend</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-700 mr-2"></div>
          <span>Full Day</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-amber-100 dark:bg-amber-900/50 border border-amber-200 dark:border-amber-700 mr-2"></div>
          <span>Morning</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-orange-100 dark:bg-orange-900/50 border border-orange-200 dark:border-orange-700 mr-2"></div>
          <span>Afternoon</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-cyan-100 dark:bg-cyan-900/50 border border-cyan-200 dark:border-cyan-700 mr-2"></div>
          <span>Half Day</span>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading shifts...</span>
        </div>
      )}

      {/* Calendar Grid */}
      {!isLoading && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            {WEEKDAYS.map((day, index) => (
              <div
                key={day}
                className={`py-3 text-center text-sm font-semibold ${
                  index === 0 || index === 6 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="min-h-[100px] bg-gray-50 dark:bg-gray-800 border-b border-r border-gray-100 dark:border-gray-700"></div>;
              }

              const shift = getShiftForDate(date);
              const holiday = getHolidayForDate(date);
              const isTodayDate = isToday(date);
              const isWeekendDate = isWeekend(date);

              return (
                <div
                  key={date.toISOString()}
                  className={`min-h-[100px] p-2 border-b border-r border-gray-100 dark:border-gray-700 transition-colors ${
                    holiday
                      ? 'bg-emerald-50 dark:bg-emerald-900/30'
                      : isWeekendDate
                      ? 'bg-gray-50 dark:bg-gray-800'
                      : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {/* Date Number */}
                  <div className="flex items-start justify-between">
                    <span
                      className={`inline-flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full ${
                        isTodayDate
                          ? 'bg-purple-500 text-white'
                          : isWeekendDate
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {date.getDate()}
                    </span>
                  </div>

                  {/* Holiday Badge */}
                  {holiday && (
                    <div className="mt-1">
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 rounded border border-emerald-200 dark:border-emerald-700">
                        {holiday.name}
                      </span>
                    </div>
                  )}

                  {/* Shift Badge */}
                  {shift && !holiday && (
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${getShiftColor(shift.shift_type as ShiftType)}`}>
                        {getShiftLabel(shift.shift_type as ShiftType)}
                      </span>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {shift.hours_worked.toFixed(1)}h
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkShiftCalendar;
